/**
 * Created by jacky on 3/6/16.
 * @flow
 * @providesModule Axis
 */

function removeFromArray(src: Array<any>, id: number): Array<any> {
    let idx = src.indexOf(id);
    if (idx > -1) {
        src.splice(idx, 1);
    }
    return src;
}

function $last(src: Array<any>): any {
    return src[src.length - 1];
}

function $unique ($src: Array<any>): Array<any> {
    let tmp = Object.create(null);

    for (let i = $src.length; --i >= 0;) {
        tmp[$src[i]] = true;
    }

    return Object.keys(tmp).map((p: any) => { return p * 1; });
}
function $min (src: Array<any>) {
    let m = void 0;
    (src || []).forEach((e) => {
        if (m != void 0 && m > e || m === void 0) {
            m = e;
        }
    });
    return m;
}

function $max (src: Array<any>) {
    var m = void 0;
    (src || []).forEach((e) => {
        if (m != void 0 && m < e || m === void 0) {
            m = e;
        }
    });
    return m;
}

export class Axis {
    _margin: number;
    _step: number;
    _axisStep: number;
    _startId: number;

    _container: Array<any> = [];
    _hash: Map<number, number> = new Map;

    _cached: Map<number, number> = new Map;
    _cachedRevert: Map<number, number>;
    _initQty: number;

    $changed: boolean;

    get count(): number {
        return this._container.length;
    }

    constructor(margin: number, startId: number, step: number, qty: number, axisStep: number) {
        this._margin = margin || 0;
        this._step = step || 1;
        this._axisStep = axisStep || 1;
        this._startId = startId || this._axisStep;
        this._initQty = qty || 0;

        this.init();
    }

    get margin():number {
        return this._margin;
    }

    set margin(value:number) {
        this._margin = value;
    }

    init(): void {
        this._container = [];
        this._hash = new Map;
        this.$changed = true;

        if (this._initQty) {
            for (let t = this._startId, i = t; i < t + (this._initQty + 1) * this._axisStep; i += this._axisStep) {
                this._container.push(i);
            }
        }
    }

    add(id: number): boolean {
        if (this._hash.get(id) === undefined || this._container.indexOf(id) == -1) {
            this._container.push(id);
            this._hash.clear();
            this.$changed = true;
            return true;
        }
        return false;
    }

    remove(id: number): void {
        removeFromArray(this._container, id);
        this._hash.clear();
        this.$changed = true;
    }

    axisById(id: number): number {
        let i = this._hash.length ? this._hash.get(id) : this._container.indexOf(id);
        if (i == undefined) {
            this.add(id);
            this.balanceSpace();
            this.update();
            return this.axisById(id);
        }
        return i * this._step + this._margin;
    }

    idByAxis(x: number, create?: boolean): number | void {
        let id = (x - this._margin) / this._step;

        if (this._container[id] === undefined) {
            let f = this._container[0];
            let fc = this._margin;
            let l = $last(this._container);
            let lc = (this._container.length - 1) * this._step + this._margin;

            if (fc > x || lc < x) {
                if (lc < x) {
                    let sr = (x - lc) / this._step;
                    this.append(~~sr < sr && ~~sr + 1 || ~~sr)
                } else if (fc > x) {
                    let sr = (fc - x) / this._step;
                    sr = (~~sr < sr) ? ~~sr - 1 : ~~sr;

                    for (var k = f - this._axisStep; sr-- > 0; k -= this._axisStep) {
                        this._container.push(k)
                    }

                    if (x < this._margin) {
                        this._margin = x;
                    }
                    id = 0;
                }
                this.update();
            }
        }

        return this._container[id];
    }

    update(adjustMargin?: boolean) {
        let mid = 0;
        let ma;
        if (adjustMargin) {
            mid = this._container[0];
            ma = this._margin;
        }

        this._hash.clear();
        this._container = $unique(this._container.sort(function (a, b) { return a - b }));

        this._container.forEach((k, i) => {
            this._hash.set(k, i);
        });

        this.$changed = false;

        if (adjustMargin && mid > this._container[0]) {
            var om = this.axisById(mid),
                nm = this.axisById(this._container[0]);
            this._margin -= om - nm;
        }
    }

    balanceSpace(): Axis {
        if (this.count == 0) { return this; }

        let f: number = $min(this._container) || 0;
        let l: number = $max(this._container) || 0;

        let i: number = ~~(f / this._axisStep) * this._axisStep;
        while (l > i) {
            this._container.push(i);
            i += this._axisStep;
        }

        this._hash.clear();
        this.$changed = true;

        return this;
    }

    append(qty: number): Axis {
        let mn = $last(this._container);
        let t = mn / this._axisStep;

        t = (t != ~~t)
            ? ~~(t) * this._axisStep
            : mn;

        for (let i = t + this._axisStep; i < t + (qty + 1) * this._axisStep; i += this._axisStep) {
            this._container.push(i);
        }

        this._hash.clear();
        this.$changed = true;

        return this;
    }

    clear() {
        this._container = [];
        this._hash.clear();
        return this;
    }

    max() {
        return $max(this._container);
    }

    min() {
        return $min(this._container);
    }

    last() {
        return $last(this._container);
    }

    first() {
        return this._container[0];
    }
}

export class AxisGrid {
    x: Axis;
    y: Axis;
    $updated: boolean;

    constructor(marginX: number, marginY: number, startIdX: number, startIdY: number, stepX: number, stepY: number, qtyX: number, qtyY: number, axisStepX: number, axisStepY: number) {
        this.x = new Axis(marginX, startIdX, stepX, qtyX, axisStepX);
        this.y = new Axis(marginY, startIdY, stepY, qtyY, axisStepY);
        this.$updated = false;
    }

    margin(marginX: number, marginY: number) {
        if (marginX == marginY == undefined) {
            return {
                left: this.x.margin,
                top: this.y.margin
            }
        }
        if (marginX == this.x.margin && marginY == this.y.margin) return;

        this.x.margin = marginX;
        this.y.margin = marginY;
        this.x.update();
        this.y.update();
    };

    addRow(qty: number) {
        this.y.append(qty).update();
    }

    addCol(qty: number) {
        this.x.append(qty).update();
    }

    insertRow(id: number) {
        this.y.add(id);
        this.y.update();
    }

    insertCol(id: number) {
        this.x.add(id);
        this.x.update();
    }

    clear() {
        this.x.clear().init();
        this.y.clear().init();
    }

    add(row: number, col: number, update?: boolean): boolean {
        let c = this.x.add(col);
        c = this.y.add(row) || c;

        if (update !== false || update == undefined && c) {
            this.x.update();
            this.y.update();
        }

        return c;
    }

    update(balanceSpace?: boolean, onlyRequired?: boolean, adjustMargin?: boolean) {
        if (balanceSpace) {
            (onlyRequired && this.x.$changed || !onlyRequired) && this.x.balanceSpace();
            (onlyRequired && this.y.$changed || !onlyRequired) && this.y.balanceSpace();
        }
        (onlyRequired && this.x.$changed || !onlyRequired) && this.x.update(adjustMargin);
        (onlyRequired && this.y.$changed || !onlyRequired) && this.y.update(adjustMargin);
        this.$updated = true;
    }

}



// WEBPACK FOOTER //
// ./src/lib/Axis/index.js